// Letters on company letterhead: the letterhead designs (read by both the on-screen
// preview and letterPdf.js), ready-made letter starters and reference numbering.
// Everything comes from the chosen business profile – nothing is tied to one company.

import { taxIdLabel } from "./document";
import { computeTotals, financialYear, fmtDate, fmtMoney, todayISO } from "./utils";

// tier: premium | simple (gallery label only)
// font: helvetica | times
export const LETTERHEADS = {
  executive: { tier: "premium", label: "Executive", desc: "Dark header band with accent rule", font: "helvetica" },
  wave: { tier: "premium", label: "Modern Wave", desc: "Bold colour corners, contemporary", font: "helvetica" },
  sidebar: { tier: "premium", label: "Sidebar", desc: "Colour edge with tinted contact footer", font: "helvetica" },
  royal: { tier: "premium", label: "Royal", desc: "Centred serif crest, double rules", font: "times" },
  corporate: { tier: "premium", label: "Corporate", desc: "Two-tone bars, contact panel", font: "helvetica" },
  classic: { tier: "simple", label: "Classic", desc: "Logo, name in colour, single rule", font: "helvetica" },
  minimal: { tier: "simple", label: "Minimal", desc: "Black & white, hairline rule", font: "helvetica" },
  centered: { tier: "simple", label: "Centred", desc: "Simple centred header and footer", font: "helvetica" },
};

export const getLetterhead = (key) => LETTERHEADS[key] || LETTERHEADS.classic;

// Header / footer text for a business profile.
export function letterheadModel(brand = {}) {
  const addr = [brand.address, brand.area, brand.city, brand.state && brand.state !== brand.city ? brand.state : "", brand.postcode].filter(Boolean).join(", ");
  const country = brand.country && !/^india$/i.test(brand.country) ? brand.country : "";
  return {
    name: brand.name || "Your Business",
    tagline: brand.tagline || "",
    logo: brand.logo || "",
    signature: brand.signature || "",
    signatory: brand.signatory || "Authorised Signatory",
    accent: brand.accent || "#C62828",
    address: [addr, country].filter(Boolean).join(", "),
    phone: brand.phone || "",
    email: brand.email || "",
    website: brand.website || "",
    ids: [brand.gstin && `${taxIdLabel(brand.gstin, brand.country)}: ${brand.gstin}`, brand.pan && `PAN: ${brand.pan}`, brand.regNo && `${brand.regLabel || "Reg. No"}: ${brand.regNo}`].filter(Boolean),
    // one-line contact strip used by most footers
    contact: [brand.phone && `Tel: ${brand.phone}`, brand.email, brand.website].filter(Boolean),
  };
}

export function suggestLetterNumber(letters, brand, date = todayISO(), settings = {}) {
  const fy = financialYear(date);
  const prefix = brand?.prefix || settings.numbering?.prefix || "REF";
  const sep = settings.numbering?.separator ?? "/";
  const taken = new Set(letters.map((l) => l.number));
  let n = letters.filter((l) => financialYear(l.date) === fy).length + 1;
  let num;
  do num = [prefix, "L", fy, n++].join(sep);
  while (taken.has(num));
  return num;
}

// Placeholders a starter can use: {client} {contact} {company} {signatory} {date}
// {doc_type} {doc_no} {doc_date} {doc_total} {doc_due} {subject}
export function letterContext({ letter, brand, client, doc, settings }) {
  const style = settings?.defaults?.dateStyle === "iso" ? "pretty" : settings?.defaults?.dateStyle || "pretty";
  const t = doc ? computeTotals(doc) : null;
  const docNoun = doc ? { quote: "quotation", proforma: "proforma invoice", credit: "credit note" }[doc.docType] || "invoice" : "";
  return {
    client: letter.to?.name || client?.name || "[Client name]",
    contact: client?.contactPerson || letter.to?.attention || "Sir / Madam",
    company: brand?.name || "[Your company]",
    signatory: letter.signName || brand?.signatory || "[Name]",
    date: fmtDate(letter.date || todayISO(), style),
    doc_type: docNoun || "[quotation / invoice]",
    doc_no: doc?.number || "[number]",
    doc_date: doc ? fmtDate(doc.date, style) : "[date]",
    doc_total: t ? fmtMoney(t.total, doc.currency, { pdf: true }) : "[amount]",
    doc_balance: t ? fmtMoney(t.balance, doc.currency, { pdf: true }) : "[amount]",
    doc_due: doc?.dueDate ? fmtDate(doc.dueDate, style) : "[date]",
    doc_subject: doc?.subject || (doc?.items || []).map((l) => l.description).filter(Boolean).slice(0, 3).join(", ") || "[the work]",
  };
}

export const fillPlaceholders = (text, ctx) => String(text || "").replace(/\{(\w+)\}/g, (m, k) => (k in ctx ? ctx[k] : m));

export const LETTER_STARTERS = [
  { id: "blank", label: "Blank letter", icon: "📝", subject: "", body: "" },
  {
    id: "quote-cover",
    label: "Quotation cover letter",
    icon: "📄",
    needsDoc: true,
    subject: "Our quotation {doc_no} for {doc_subject}",
    body: `Thank you for your enquiry and for the opportunity to submit our offer.

Please find enclosed our {doc_type} {doc_no} dated {doc_date} for {doc_subject}, amounting to {doc_total}. The quotation sets out the scope of supply, specifications, commercial terms and validity.

We are confident that our solution will meet your requirements in quality, timelines and value. We would be glad to walk you through the proposal, arrange a site visit or adjust the scope to suit your budget.

We look forward to your valued order.`,
    enclosures: "Quotation {doc_no}",
  },
  {
    id: "payment-reminder",
    label: "Payment reminder",
    icon: "⏰",
    needsDoc: true,
    subject: "Reminder: payment due against {doc_type} {doc_no}",
    body: `This is a gentle reminder that our {doc_type} {doc_no} dated {doc_date} for {doc_total} was due for payment on {doc_due}. According to our records, {doc_balance} is still outstanding.

We request you to arrange the payment at the earliest. Our bank details are mentioned on the invoice. If you have already made the payment, please share the transaction reference so that we can update our records.

Thank you for your continued business.`,
  },
  {
    id: "order-thanks",
    label: "Order confirmation / thank you",
    icon: "🤝",
    subject: "Thank you for your order",
    body: `Thank you for choosing {company}. We are pleased to confirm receipt of your order and appreciate the trust you have placed in us.

Our team has begun processing it and will keep you informed at every stage. For any queries, please feel free to contact us.

We look forward to a long and successful association.`,
  },
  {
    id: "completion",
    label: "Work completion certificate",
    icon: "✅",
    subject: "Work completion certificate",
    body: `This is to certify that {company} has successfully completed the work of [description of work] at [site / address] for {client}.

The work was started on [start date] and completed on [completion date] in accordance with the agreed scope, specifications and quality standards. All systems have been tested and handed over in good working condition.

This certificate is issued on request for whatever purpose it may serve.`,
  },
  {
    id: "price-revision",
    label: "Price revision notice",
    icon: "📈",
    subject: "Revision of prices with effect from [date]",
    body: `We value our association with you and thank you for your continued support.

Due to a steady increase in the cost of raw materials, freight and operations, we are compelled to revise our prices by [x]% with effect from [date]. Orders confirmed before this date will be honoured at the existing prices.

We assure you that we will continue to deliver the same quality and service you have come to expect from {company}.`,
  },
  {
    id: "authorization",
    label: "Authorisation letter",
    icon: "🖊️",
    subject: "Letter of authorisation",
    body: `We, {company}, hereby authorise Mr./Ms. [name], [designation], holding [ID proof and number], to act on our behalf for [purpose – e.g. submitting documents, collecting material, attending meetings] with {client}.

Any document signed or action taken by the above person for this purpose shall be binding on us.

The specimen signature of the authorised person is given below.`,
  },
  {
    id: "introduction",
    label: "Company introduction",
    icon: "🏢",
    subject: "Introduction of {company}",
    body: `We take this opportunity to introduce {company}, a [type of business] serving clients across [region] since [year].

We specialise in [key products / services]. Our strengths include experienced people, quality assurance, on-time delivery and dependable after-sales support. We have served [number] customers including [notable clients].

We would welcome the opportunity to discuss how we can support your requirements and would be happy to share a detailed quotation.`,
  },
  {
    id: "offer-letter",
    label: "Offer letter (employment)",
    icon: "💼",
    subject: "Offer of employment",
    body: `We are pleased to offer you the position of [designation] at {company}, based at [location], with effect from [joining date].

Your total annual compensation will be [amount], details of which are given in the annexure. You will be on probation for [period], after which your employment will be confirmed subject to satisfactory performance.

Please sign and return a copy of this letter as a token of your acceptance. We look forward to welcoming you to the team.`,
  },
  {
    id: "experience",
    label: "Experience certificate",
    icon: "🎖️",
    subject: "Experience certificate",
    body: `This is to certify that Mr./Ms. [name] was employed with {company} as [designation] from [joining date] to [relieving date].

During this period we found him/her sincere, hardworking and dedicated to the work assigned. His/her conduct was good.

We wish him/her every success in future endeavours.`,
  },
  {
    id: "noc",
    label: "No objection certificate (NOC)",
    icon: "📜",
    subject: "No objection certificate",
    body: `This is to certify that {company} has no objection to [name / organisation] for [purpose].

This certificate is issued at the request of the applicant and is valid for the stated purpose only.`,
  },
];

export const starterById = (id) => LETTER_STARTERS.find((s) => s.id === id);
